'use client';

import Link from 'next/link';

interface PublicRoadmapItem {
  id: string;
  title: string;
  voteCount: number;
  hasVoted: boolean;
  featureRequestId?: string;
}

interface PublicRoadmapBoardProps {
  planned: PublicRoadmapItem[];
  inProgress: PublicRoadmapItem[];
  completed: PublicRoadmapItem[];
  portalSlug: string;
}

const COLUMNS = [
  {
    key: 'planned',
    label: 'Planned',
    headerColor: 'bg-purple-500',
    textColor: 'text-purple-600 dark:text-purple-400',
  },
  {
    key: 'inProgress',
    label: 'In Progress',
    headerColor: 'bg-orange-500',
    textColor: 'text-orange-700 dark:text-orange-400',
  },
  {
    key: 'completed',
    label: 'Completed',
    headerColor: 'bg-green-500',
    textColor: 'text-green-700 dark:text-green-400',
  },
];

function RoadmapItemCard({
  item,
  portalSlug,
}: {
  item: PublicRoadmapItem;
  portalSlug: string;
}) {
  const content = (
    <div className="bg-card rounded-xl border border-border/60 p-4 hover:shadow-md hover:border-primary/20 transition-all duration-200">
      <p className="text-sm font-medium mb-2">{item.title}</p>
      <div className="flex items-center gap-1.5">
        <span
          className={`inline-flex items-center gap-1 text-xs ${
            item.hasVoted
              ? 'text-[var(--portal-primary,#6366f1)] font-medium'
              : 'text-muted-foreground'
          }`}
        >
          <svg
            className="w-3.5 h-3.5"
            fill={item.hasVoted ? 'currentColor' : 'none'}
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 15l7-7 7 7"
            />
          </svg>
          {item.voteCount}
        </span>
      </div>
    </div>
  );

  if (item.featureRequestId) {
    return (
      <Link href={`/p/${portalSlug}/${item.featureRequestId}`}>
        {content}
      </Link>
    );
  }

  return content;
}

export function PublicRoadmapBoard({
  planned,
  inProgress,
  completed,
  portalSlug,
}: PublicRoadmapBoardProps) {
  const columnData: Record<string, PublicRoadmapItem[]> = {
    planned,
    inProgress,
    completed,
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {COLUMNS.map((col) => (
        <div key={col.key}>
          <div className="flex items-center gap-2 mb-4">
            <div className={`w-2.5 h-2.5 rounded-full ${col.headerColor}`} />
            <h2 className={`text-sm font-semibold ${col.textColor}`}>
              {col.label}
            </h2>
            <span className="text-xs text-muted-foreground ml-auto">
              {columnData[col.key].length}
            </span>
          </div>
          <div className="space-y-3">
            {columnData[col.key].map((item) => (
              <RoadmapItemCard
                key={item.id}
                item={item}
                portalSlug={portalSlug}
              />
            ))}
            {columnData[col.key].length === 0 && (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mb-2">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-muted-foreground">
                    <rect x="3" y="3" width="18" height="18" rx="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M12 8v8M8 12h8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <p className="text-xs text-muted-foreground">No items yet</p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
